<template>
  <Modal v-model="shown" :header="true" :backdrop="backDropMode">
    <template #title>
      <h1
        v-if="
          displayMode === 'first-plan' ||
          displayMode === 'changing-plan' ||
          displayMode === 'returning-to-this-plan' ||
          displayMode === 'in-progress'
        "
      >
        Develop {{ strengthData.name }}?
      </h1>

      <h1 v-if="displayMode === 'not-completed-solo'" data-test="sp-no-solo-title">
        Complete a Solo review
      </h1>
      <h1 v-if="displayMode === 'on-this-plan'" data-test="sp-same-plan-title">
        Continue developing {{ strengthData.name }}?
      </h1>
      <h1 v-if="displayMode === 'set'">{{ strengthData.name }}</h1>
    </template>

    <!-- IN PROGRESS SECTION -->
    <div v-if="inProgress === true">
      <br />
      <br />
      <LoadingSpinner />
      <br />
      <br />
    </div>
    <!-- FIRST PLAN SECTION -->
    <div v-if="displayMode === 'first-plan' && !inProgress && !isSet">
      <p>
        Are you ready to develop <strong>{{ strengthData.name }}?</strong>
      </p>
      <br />
      <p>
        Click <strong>yes</strong> and you'll be taken to the {{ strengthData.name }} 25-day
        development plan.
      </p>
      <p>Not sure? Click <strong>cancel</strong> and choose a different Strength.</p>
    </div>
    <!-- CHANGING PLAN SECTION -->
    <div v-if="displayMode === 'changing-plan' && !inProgress && !isSet" data-test="sp-change-text">
      <p>
        Are you ready to develop <strong>{{ strengthData.name }}?</strong> This will replace your
        current development plan <strong>{{ currentDevelopPlan.strengthItemId }}.</strong> But don't
        worry! You can always pick up <strong>{{ currentDevelopPlan.strengthItemId }}</strong> at a
        later date, exactly where you left off.
      </p>
      <br />
      <p>
        If you want to continue to develop <strong>{{ currentDevelopPlan.strengthItemId }}</strong>
        click
        <strong>cancel</strong>. We recommend
        <a
          href="/resources/read/dealing-with-obstacles-and-flagging-motivation"
          data-test="sp-resource-link"
          >this resource</a
        >
        if you've fallen off the development wagon!
      </p>
    </div>
    <!-- NOT COMPLETED SOLO SECTION -->
    <div v-if="displayMode === 'not-completed-solo'">
      <p>Oops! It looks like you haven't completed a Solo review yet.</p>
      <br />
      <p>The <strong>Personal Journey Pathway</strong> will guide you to create a Solo report.</p>
      <p>Then you'll be ready to get the best from the Gyre Plans.</p>
      <br />
      <p>Click <strong>Let's go</strong> for the <strong>Personal Journey Pathway.</strong></p>
    </div>
    <!-- ALREADY ON THIS PLAN SECTION -->
    <div v-if="displayMode === 'on-this-plan'" class="text-center">
      <p v-if="currentDevelopPlan.lastCompletedDayIndex < 25" data-test="sp-same-plan-text">
        You are currently on day {{ currentDevelopPlan.lastCompletedDayIndex + 1 }} of 25.
      </p>
      <p v-else>You’ve already completed this develop plan.</p>
    </div>
    <!-- RETURNING TO THIS PLAN SECTION -->
    <div v-if="displayMode === 'returning-to-this-plan' && !inProgress && !isSet">
      <p>
        Are you ready to develop <strong>{{ strengthData.name }}?</strong> This will replace your
        current development plan <strong>{{ currentDevelopPlan.strengthItemId }}.</strong> But don't
        worry! You can always pick up {{ currentDevelopPlan.strengthItemId }} at a later date,
        exactly where you left off.
      </p>
      <br />
      <p v-if="previousPlansProgress && previousPlansProgress < 25">
        It looks like you're already on day {{ previousPlansProgress + 1 }} of
        <strong>{{ strengthData.name }}.</strong>
      </p>
      <p v-else>You’ve already completed this develop plan.</p>
    </div>

    <!-- SET SECTION -->
    <div v-if="displayMode === 'set'">
      <br />
      <div class="image-container">
        <img src="../../assets/Tick-GIF.gif" alt="Plan selected" />
        <p>Good luck on your journey!</p>
      </div>
      <br />
    </div>

    <template #footer>
      <!--      FIRST PLAN & CHANGING PLAN & IN PROGRESS BUTTONS-->
      <div
        v-if="displayMode === 'first-plan' || displayMode === 'changing-plan'"
        class="controls center-flex"
      >
        <button class="btn btn-default" @click="closeModal" data-test="sp-cancel" type="button">
          Cancel
        </button>
        <button
          class="btn btn-primary"
          @click="continueWithSelectedPlan"
          data-test="sp-select-plan"
          type="button"
        >
          Yes
        </button>
      </div>

      <!--      NOT COMPLETED SOLO BUTTONS-->
      <div v-if="displayMode === 'not-completed-solo'" class="controls center-flex">
        <button class="btn btn-default" @click="closeModal" type="button">Cancel</button>
        <a href="/Session/SoloReport" class="btn btn-primary" data-test="sp-no-solo-btn">
          Let's go
        </a>
      </div>

      <!--      ALREADY ON THIS PLAN-->
      <div v-if="displayMode === 'on-this-plan'" class="controls center-flex">
        <button class="btn btn-default" @click="closeModal" type="button">Cancel</button>
        <button class="btn btn-primary" @click="$router.push('/develop')" type="button">
          Continue
        </button>
      </div>

      <!--      RETURNING TO THIS PLAN BUTTONS-->
      <div v-if="displayMode === 'returning-to-this-plan'" class="controls center-flex">
        <button class="btn btn-default" @click="closeModal" type="button">Cancel</button>
        <button class="btn btn-primary" @click="continueWithSelectedPlan" type="button">
          Continue
        </button>
      </div>

      <!--      IN PROGRESS -->
      <div v-if="displayMode === 'in-progress'" class="controls center-flex">
        <button class="btn btn-primary" disabled type="button">Continue</button>
      </div>

      <!--      SET BUTTONS-->
      <div v-if="displayMode === 'set'" class="controls center-flex">
        <button
          class="btn btn-primary"
          @click="$router.push('/develop')"
          data-test="spm-continue-btn"
          type="button"
        >
          Continue
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Modal } from "uiv";
import axios from "axios";
import LoadingSpinner from "../loading-spinner.vue";

type DisplayVariant =
  | "first-plan"
  | "returning-to-this-plan"
  | "changing-plan"
  | "on-this-plan"
  | "not-completed-solo"
  | "set"
  | "in-progress";

const props = defineProps<{
  strengthData: any;
  currentDevelopPlan: any;
  isSoloCompleted: string;
  subscriptionLevel: string;
  otherProgress: any[];
}>();

const shown = ref(false);
const inProgress = ref(false);
const isSet = ref(false);

const isThisPlanWasInProgressBefore = computed(() => {
  return props.otherProgress.some((e) => e.strengthItemId === props.strengthData.name);
});

const previousPlansProgress = computed(() => {
  if (isThisPlanWasInProgressBefore.value) {
    const data = props.otherProgress.filter((e) => e.strengthItemId === props.strengthData.name);
    return data[0].lastCompletedDayIndex;
  }
  return null;
});

const hasCompletedCurrentPlan = computed(() => {
  const totalDevelopDays = 25;
  return props.currentDevelopPlan.lastCompletedDayIndex === totalDevelopDays;
});

const displayMode = computed(() => {
  let displayVariant: DisplayVariant = "first-plan";

  if (props.isSoloCompleted === "Unlocked") {
    if (props.subscriptionLevel === "Full") {
      if (!props.currentDevelopPlan || hasCompletedCurrentPlan.value) {
        displayVariant = "first-plan";
        if (isSet.value) {
          displayVariant = "set";
          return displayVariant;
        }
        return displayVariant;
      }

      if (
        props.currentDevelopPlan.strengthItemId !== props.strengthData.name &&
        isThisPlanWasInProgressBefore.value
      ) {
        displayVariant = "returning-to-this-plan";
      } else {
        displayVariant = "changing-plan";
      }
      if (props.currentDevelopPlan.strengthItemId === props.strengthData.name) {
        displayVariant = "on-this-plan";
      }
    }
  } else {
    displayVariant = "not-completed-solo";
  }
  if (inProgress.value) {
    displayVariant = "in-progress";
  }
  if (isSet.value) {
    displayVariant = "set";
  }
  return displayVariant;
});

const backDropMode = computed(() => {
  return !(displayMode.value === "set" || displayMode.value === "in-progress");
});

const continueWithSelectedPlan = async () => {
  inProgress.value = true;
  const response = await axios.patch(
    `/api/engagequestionnaire/UpdateDevelop/${props.strengthData.name}`,
  );
  if (response.status === 200) {
    inProgress.value = false;
    isSet.value = true;
  }
};

const openModal = () => {
  shown.value = true;
};

const closeModal = () => {
  shown.value = false;
  inProgress.value = false;
};

defineExpose({
  openModal,
  closeModal,
});

// @Component({
//   components: { LoadingSpinner, Modal },
// })
// export default class StrengthPageModal extends Vue {
//   shown = false;

//   inProgress = false;

//   isSet = false;

//   displayVariant:
//     | "first-plan"
//     | "returning-to-this-plan"
//     | "changing-plan"
//     | "on-this-plan"
//     | "not-completed-solo"
//     | "set"
//     | "in-progress" = "first-plan";

//   @Prop()
//   strengthData;

//   @Prop()
//   currentDevelopPlan;

//   @Prop()
//   isSoloCompleted;

//   @Prop()
//   subscriptionLevel;

//   @Prop()
//   otherProgress;

//   get isThisPlanWasInProgressBefore(): boolean {
//     return this.otherProgress.some((e) => e.strengthItemId === this.strengthData.name);
//   }

//   get previousPlansProgress(): number | null {
//     if (this.isThisPlanWasInProgressBefore) {
//       const data = this.otherProgress.filter((e) => e.strengthItemId === this.strengthData.name);
//       return data[0].lastCompletedDayIndex;
//     }
//     return null;
//   }

//   get displayMode(): string {
//     // Check the user completed solo
//     if (this.isSoloCompleted === "Unlocked") {
//       // Check the account has full access
//       if (this.subscriptionLevel === "Full") {
//         if (!this.currentDevelopPlan || this.hasCompletedCurrentPlan) {
//           this.displayVariant = "first-plan";
//           if (this.isSet) {
//             this.displayVariant = "set";
//             return this.displayVariant;
//           }
//           return this.displayVariant;
//         }

//         if (
//           this.currentDevelopPlan.strengthItemId !== this.strengthData.name &&
//           this.isThisPlanWasInProgressBefore
//         ) {
//           this.displayVariant = "returning-to-this-plan";
//         } else {
//           this.displayVariant = "changing-plan";
//         }
//         if (this.currentDevelopPlan.strengthItemId === this.strengthData.name) {
//           this.displayVariant = "on-this-plan";
//         }
//       }
//     } else {
//       this.displayVariant = "not-completed-solo";
//     }
//     if (this.inProgress) {
//       this.displayVariant = "in-progress";
//     }
//     if (this.isSet) {
//       this.displayVariant = "set";
//     }
//     return this.displayVariant;
//   }

//   get backDropMode(): boolean {
//     return !(this.displayMode === "set" || this.displayMode === "in-progress");
//   }

//   async continueWithSelectedPlan(): Promise<void> {
//     this.inProgress = true;
//     const response = await axios.patch(
//       `/api/engagequestionnaire/UpdateDevelop/${this.strengthData.name}`,
//     );
//     if (response.status === 200) {
//       this.inProgress = false;
//       this.isSet = true;
//     }
//   }

//   get hasCompletedCurrentPlan(): boolean {
//     const totalDevelopDays = 25;
//     return this.currentDevelopPlan.lastCompletedDayIndex === totalDevelopDays;
//   }

//   openModal(): void {
//     this.shown = true;
//   }

//   closeModal(): void {
//     this.shown = false;
//     this.inProgress = false;
//   }
// }
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

h1 {
  text-align: center;
}

p {
  margin: 1rem 3rem;
  font-size: 1.8rem;
}

.image-container {
  text-align: center;

  img {
    width: 10%;
    height: 50%;
  }
}
</style>
